<template lang="pug">
div
	h1 {{accommodation.name}} {{'*'.repeat(accommodation.stars)}}
	glue-list(glue=" - " lastglue=" - ")
		magic-nuxt-link(:to="accommodation.resort.country")
			img.flag(:src="`https://static.wintersport.nl/images/flags/svg/${accommodation.resort.country.countryCode}.svg`")
			| {{accommodation.resort.country.name}}
		magic-nuxt-link(:to="accommodation.resort.area") {{accommodation.resort.area.name}}
		magic-nuxt-link.font-weight-bold(:to="accommodation.resort") {{accommodation.resort.name}}

	gallery-embed.my-3(:photos="galleryImagesWithCaption" fill showCaptions display="grid")
		template(#tile4)
			v-img(:src="require('~/assets/images/acco_detail_map.jpg')" :aspect-ratio="4/3" @click="gotoMap")
				.d-flex.align-center.justify-center(style="height:100%; cursor:pointer")
					.text-h5.white--text.font-weight-bold(style="text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.5)")
						v-icon(large color="white") search
						| toon op kaart

	v-container.pa-0
		v-row.mb-6.text-subtitle-1.font-weight-regular
			v-col(cols="12" md="auto" v-if="accommodation.rating")
				ws-icon(color="success") check
				| Beoordeeld met een
				|
				v-chip.px-1.white--text.py-0.text-subtitle-1.font-weight-bold(label small :color="ratingcolor") {{accommodation.rating < 10 ? accommodation.rating.toFixed(1): 10}}

			v-col(cols="12" md="auto")
				ws-icon(color="black") map-marker
				span {{formatDistance(accommodation.distanceToCenter)}} van het centrum {{accommodation.resort.name}}
			v-col(cols="12" md="auto" v-if="skilifts.length")
				ws-icon(color="black") {{skilifts[0].icon}}
				span {{formatDistance(skilifts[0].distance)}} tot skilift {{skilifts[0].name}}

	.mb-6
		h2 Over dit {{$t(`accommodations-filters.${accommodation.type}`).toLowerCase()}}
		template(v-if="accommodation.facilities.length")
			v-divider.my-3
			info-paragraph(title="Voorzieningen")
				factline.black--text.mb-3(vertical)
					li(v-for="facility in accommodation.facilities" :key="facility" :icon="{swimming: 'swimming-pool', hottub:'hot-tub'}[facility] || facility") {{$t(`accommodations-filters.${facility}`)}}

		template(v-if="accommodation.descriptionPerPartner && accommodation.descriptionPerPartner.length")
			v-divider.my-3
			info-paragraph(title="Overige info")
				template(v-for="description in descriptionPerPartner")
					.caption.font-weight-bold(v-if="description.partner.name !== 'DS'") Geschreven door {{description.partner.name}}:
					.caption.font-weight-bold(v-else)
						| Beschrijving van de aanbieder:
						a.ml-4(@click="showTranslatedDescription=true; descriptionReadMore=true" v-if="!showTranslatedDescription") bekijk originele tekst
					ws-content(v-if="description.description.markdown && descriptionReadMore" :value="description.description")
					p.mb-2(v-if="description.description.markdown && !descriptionReadMore") {{accommodation.descriptionPerPartner[0].description.introduction}}
					template(v-if="description.description.markdown !== description.original.markdown")
							div.font-italic(v-if="showTranslatedDescription")
								v-divider.my-6
								ws-content(v-if="description.original.markdown" :value="description.original")
				.d-flex.align-center.justify-center(v-if="!descriptionReadMore && accommodation.descriptionPerPartner.length > 0")
					v-btn(@click="descriptionReadMore = true" color="primary" outlined small) Toon meer

	client-only
		.mb-12(v-if="filterstring")
			h2 Deze opties hebben we voor je gevonden
			v-menu(offset-y :close-on-content-click="false" max-width="300" nudge-bottom="12" v-model="showFilterDialog")
				v-card.pa-2(color="info" outlined)
					minimum-filter(:duration="filters.duration" :persons="filters.persons" :date="filters.date" @update="updateFilters")
						template(#action="{update}")
							v-btn(color="accent" depressed block @click="update") kamers zoeken
				template(#activator="{on}")
					p
						| Alle resultaten op basis van je zoekopdracht van
						|
						a(v-on="on") {{filters.duration}} overnachtingen, {{filters.persons}} personen met aankomst op {{$dayjs(filters.date).format('D MMMM YYYY')}}
			variant-table.mt-3(:accommodation-id="accommodation.id" :filterstring="filterstring" @select-variant="abortAlternatives" hideBreakdown ref="varianttable")
				template(#action="{item}")
					.py-3.text-center
						v-btn.font-weight-bold(color="warning" depressed medium style="font-size:13") checken
						.caption.accent--text.pt-2(style="font-size:11px !important; line-height:11px !important" v-if="item.checkout === 'ONSITE'")
							v-icon(x-small color="accent") check
							|
							| boek direct op wintersport.nl
		div(v-else)
			h2 Zoeken naar kamers
			v-card.pa-2.mb-6(color="info" outlined)
				minimum-filter(:duration="filters.duration" :persons="filters.persons" :date="filters.date" @update="updateFilters" horizontal)
					template(#action="{update}")
						v-btn(color="accent" depressed block @click="update") kamers zoeken
	client-only
		div(v-if="filterstring")
			h2
				ws-icon(color="black") tags
				| Prijzen in andere weken
			p Ben je flexibel in je vakantiedata? Wellicht kun je een stuk goedkoper op vakantie! Check hieronder de prijzen in andere weken.
			variant-other-weeks.mb-6(:accommodation-id="accommodation.id" :filterstring="filterstring" ref="otherweeks")

	h2
		ws-icon(color="black") map-marker
		| Locatie in het dorp en skigebied
	p
		| {{accommodation.name}} bevindt zich {{formatDistance(accommodation.distanceToCenter)}} van het centrum van {{accommodation.resort.name}}
		|
		|
		template(v-if="skilifts.length") en {{formatDistance(skilifts[0].distance)}} tot skilift {{skilifts[0].name}}.
		template(v-else) maar er liggen helaas geen skiliften in de buurt.
	client-only
		map-nearby.mb-12(:accommodation-id="accommodation.id" :filterstring="mapFilterstring" v-on:map-marker-click="onClickMarker" ref="mapnearby")

	h2
		v-icon(color="black") info
		|
		| Goed om te weten over het skigebied {{accommodation.resort.area.name}}
	p Dit skigebied ligt op <strong>{{area.departures.city.distance}}km rijden van {{$store.getters.preferedCity.charAt(0).toUpperCase() + $store.getters.preferedCity.slice(1)}}</strong>. Je vind hier totaal <strong>{{area.pistes.total}}km aan pistes</strong>, verdeeld over {{area.pistes.slopeBlue}}km blauwe pistes, {{area.pistes.slopeRed}}km rode pistes en {{area.pistes.slopeBlack}}km aan zwarte pistes. Het laagste punt van het skigebied vind je op <em>{{area.minElevation}}m</em> en het hoogste punt ligt op <em>{{area.maxElevation}}m</em>. Meer weten over {{accommodation.resort.area.name}}? Check dan onze <magic-nuxt-link :to="accommodation.resort.area">skigebiedpagina</magic-nuxt-link>!

	template(v-if="accommodation.resort.rentalProducts.length")
		h2.mt-12 Skihuur
		v-simple-table
			template(#default)
				thead
					tr
						th {{$t('page.areas._area.rental:rental_provider')}}
						th(colspan="6") {{$t('page.areas._area.rental:rental_location')}}
				tbody
					template(v-for="rentalProduct in accommodation.resort.rentalProducts")
						tr
							td
								a(:href="`/redir?u=${encodeURIComponent(rentalProduct.shop.url)}&p=${rentalProduct.shop.partner.slug}&i=${rentalProduct.shop.id}&t=rental&h=${$redirHash(rentalProduct.shop.url)}`" target="_blank" rel="nofollow" @click="$gtag.event('click', {event_label:'check-rental-product', event_category:'accommodations-search-id'})")
									v-img(:src="rentalProduct.shop.partner.image" :alt="rentalProduct.shop.partner.name" class="partner-logo"  width="70" height="24" contain)
							td {{rentalProduct.shop.name}} ({{rentalProduct.shop.city}})
							td {{rentalProduct.name}}
							td
								img(:src="rentalProduct.image" width="380")
							td {{ $n(rentalProduct.price, 'currency', $i18n.locale) }}
							td(align="right" width="70")
								v-btn(small depressed color="warning" :href="`/redir?u=${encodeURIComponent(rentalProduct.shop.url)}&p=${rentalProduct.shop.partner.slug}&i=${rentalProduct.shop.id}&t=rental&h=${$redirHash(rentalProduct.shop.url)}`" target="_blank" rel="nofollow" @click="$gtag.event('click', {event_label:'check-rental-product', event_category:'accommodations-search-id'})") {{$t('page.areas._area.rental:check_button')}}

	template(v-if="accommodation.resort.skischoolProducts.length")
		h2.mt-12 Skiles
		v-simple-table
			template(#default)
				tbody
					template(v-for="skischoolProduct in accommodation.resort.skischoolProducts")
						tr
							td
								v-img(:src="skischoolProduct.school.partner.image" width="70" height="24" contain)
							td {{skischoolProduct.school.name}}
							td {{skischoolProduct.name}}
							td
								span(v-if="skischoolProduct.school.partner.slug === 'checkyeti'") {{$t('page.areas._area.lessons:price_prefix')}}&nbsp;
								span(v-else) {{$t('page.areas._area.lessons:price_prefix_from')}}&nbsp;
								span(v-if="skischoolProduct.school.partner.slug === 'checkyeti'").warning--text.font-weight-bold {{$n(skischoolProduct.price, 'simplecurrency', $i18n.locale)}} per dag
								span(v-else).warning--text.font-weight-bold {{$n(skischoolProduct.price, 'simplecurrency', $i18n.locale)}}
							td(align="right" width="70")
								v-btn(small depressed color="warning" :href="`/redir?u=${encodeURIComponent(skischoolProduct.school.url)}&p=${skischoolProduct.school.partner.slug}&i=${skischoolProduct.school.id}&t=skischool&h=${$redirHash(skischoolProduct.school.url)}`" target="_blank" rel="nofollow" @click="$gtag.event('click', {event_label:'check-skischool-product', event_category:'accommodations-search-id'})") {{$t('page.areas._area.rental:check_button')}}

	disclaimer
</template>

<script>
import gql from 'graphql-tag';
import AccommodationList from '@dengl/common/components/accommodations/list.vue';
import AccommodationRating from '@dengl/common/components/accommodations/rating.vue';
import GalleryEmbed from '@dengl/embeds/image-gallery/embed.vue';
import Disclaimer from '@dengl/common/components/accommodations/disclaimer.vue';
import VariantTable from '@/components/accommodations/variant-table.vue';
import VariantOtherWeeks from '@/components/accommodations/variant-other-weeks.vue';
import MapNearby from '@/components/accommodations/map-nearby.vue';
import InfoParagraph from '@/components/accommodations/info-paragraph.js';
import Cart from '@/components/accommodations/cart.vue';
import MinimumFilter from '@/components/accommodations/minimum-filter.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';

const {
	encodeFilters,
	decodeFilters
} = require('@dengl/common/utils/filterstrings');

const query = gql`
	query getAccommodationDetails(
		$id: ID!
		$preferedCity: String!
		$season: String!
	) {
		accommodation(id: $id) {
			id
			name
			descriptionPerPartner {
				partner {
					id
					name
				}
				description {
					markdown
					introduction
				}
				original {
					markdown
				}
			}
			type
			imagesWithCaption {
				url
				caption
			}
			location
			url
			rating
			stars
			isInresort
			facilities
			views
			distanceToCenter
			...MetaFragment
			pois {
				distance
				item {
					... on Skilift {
						id
						name
						type
						location
						geography {
							location {
								type
								coordinates
							}
						}
					}
					... on Rental {
						id
						name
						partner {
							id
							image
							name
							slug
						}
						geography {
							location {
								type
								coordinates
							}
						}
					}
					... on Skischool {
						id
						name
						partner {
							id
							image
							name
							slug
						}
						geography {
							location {
								type
								coordinates
							}
						}
					}
				}
			}
			resort {
				id
				name
				slug
				area {
					id
					name
					slug
					minElevation
					maxElevation
					pistes: slopeinfo {
						total
						slopeGreen
						slopeBlue
						slopeRed
						slopeBlack
					}
					departures {
						city(city: $preferedCity) {
							distance
							node {
								id
								name
							}
						}
					}
				}
				country {
					id
					name
					slug
					countryCode
				}
				rentalAdult: bestRentalPrice(season: $season, type: adult) {
					price
					img_ski
				}
				rentalChild: bestRentalPrice(season: $season, type: child) {
					price
					img_ski
				}
				lessonsAdult: bestLessonPrice(season: $season, type: adult) {
					price
				}
				lessonsChild: bestLessonPrice(season: $season, type: child) {
					price
				}
				rentalProducts(type: adult) {
					id
					name
					price
					image
					shop {
						id
						name
						city
						discount
						discount_text
						url
						geography {
							location {
								type
								coordinates
							}
						}
						partner {
							id
							image
							name
							slug
						}
					}
				}
				skischoolProducts(type: adult) {
					id
					url
					price
					name
					type
					school {
						id
						name
						resort_name
						url
						partner {
							id
							image
							name
							slug
						}
					}
				}
				locations(types: [restaurant, pisterestaurant, apresski, club]) {
					id
					name
					slug
					photos {
						url
						alt
					}
					properties
					reviews {
						voteCount
						average
					}
					geography {
						location {
							type
							coordinates
						}
					}
				}
			}
		}
	}
	${MetaFragment}
`;

const tripCreateMutation = gql`
	mutation ($availabilityId: String!) {
		tripCreate(availabilityId: $availabilityId) {
			id
		}
	}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL':
				'/accommodatie/:id([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})',
			'nl-BE':
				'/accommodatie/:id([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'
		}
	},
	name: 'AccommodationsDetail',
	components: {
		AccommodationList,
		AccommodationRating,
		InfoParagraph,
		VariantTable,
		VariantOtherWeeks,
		Cart,
		MinimumFilter,
		GalleryEmbed,
		MapNearby,
		Disclaimer
	},
	asyncData({ app, params, route }) {
		return app.$graphClient
			.query({
				query,
				variables: {
					id: params.id,
					preferedCity: app.store.getters.preferedCity,
					season: '2021' // todo: dynamisch maken op basis van departure date?
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.accommodation) {
					return app.httpError(404, null, 'accommodations-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.meta(data.accommodation.meta);
				app.ads({});
				app.breadcrumbs({
					accommodaties: { name: 'accommodations-search', hash: route.hash },
					[data.accommodation.name]: {
						name: 'click',
						params: {
							id: data.accommodation.id
						}
					}
				});

				return data;
			})
			.catch(error => {
				return app.httpError(500, null, 'accommodations-02', error);
			});
	},
	data() {
		return {
			showFilterDialog: false,
			descriptionReadMore: false,
			skipastickets: [],
			customMapOptions: null,
			filters: {},
			showTranslatedDescription: false
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		skilifts() {
			return this.accommodation.pois
				.filter(poi => {
					return poi.item.__typename === 'Skilift';
				})
				.map(poi => {
					return {
						name: poi.item.name,
						distance: poi.distance,
						icon: poi.item.type
					};
				});
		},
		mapFilterstring() {
			return encodeFilters(
				{
					...this.filters,
					areaslugs: [this.accommodation.resort.area.slug]
				},
				this.$i18n.locale
			);
		},
		galleryImagesWithCaption() {
			return this.accommodation.imagesWithCaption.map(image => {
				let url = new URL(image.url);
				const imageWithoutProtocol = url.toString().replace(/https?:\/\//i, '');

				if (!url.toString().includes('bstatic.com')) {
					url = `https://cdn.wintersport.nl/proxy/${imageWithoutProtocol}`;
				}

				return {
					url,
					caption: image.caption
				};
			});
		},
		ratingcolor() {
			if (this.accommodation.rating >= 9) {
				return '#e2aa11';
			}
			if (this.accommodation.rating >= 7) {
				return 'success';
			}
			return '#999999';
		},
		area() {
			return this.accommodation.resort.area;
		},
		descriptionPerPartner() {
			if (!this.descriptionReadMore) {
				return this.accommodation.descriptionPerPartner.slice(0, 1);
			}

			return this.accommodation.descriptionPerPartner;
		}
	},
	watch: {
		'$route.hash': {
			handler(val) {
				this.gotoVariantTable();
				this.filterstring = val.replace('#', '');
				this.filters = decodeFilters(this.filterstring, this.$i18n.locale);
				this.$store.commit('SET_ACCOHASH', val);
			},
			immediate: true
		},
		'accommodation.id': {
			handler() {
				this.filterstring = this.$route.hash.replace('#', '');
				this.filters = decodeFilters(this.filterstring, this.$i18n.locale);
			},
			immediate: true
		}
	},
	mounted() {
		// if (this.$route.hash && !this.filterstring) {
		// 	this.filterstring = this.$route.hash.replace('#', '');
		// }
	},
	methods: {
		gotoVariantTable() {
			if (this.$refs.varianttable) {
				this.$refs.varianttable.$el.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center'
				});
			}
		},
		gotoMap() {
			this.$refs.mapnearby.$el.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'center'
			});
		},
		addToMyTrip(variant) {
			this.$gtag.event('click', {
				event_label: 'add-to-mytrip',
				event_category: 'accommodations-search-id'
			});

			return this.$graphClient
				.mutate({
					mutation: tripCreateMutation,
					variables: {
						availabilityId: variant.id
					},
					errorPolicy: 'all'
				})
				.then(({ data, _errors }) => {
					this.$router.push(
						this.localePath({
							name: 'accommodations-mytrip',
							params: {
								mytrip: data.tripCreate.id
							}
						})
					);
				})
				.catch(error => {
					return this.httpError(
						500,
						null,
						'accommodation-trip-create-01',
						error
					);
				});
		},
		updateFilters(filters) {
			const hashFilters = window.location.hash
				? decodeFilters(window.location.hash, this.$i18n.locale)
				: {};
			this.filterstring = encodeFilters(
				{ ...hashFilters, ...filters },
				this.$i18n.locale
			);
			this.showFilterDialog = false;
			window.location.hash = `#${this.filterstring}`;
		},
		formatDistance(distance) {
			if (distance < 10) {
				return '5m kruipen';
			} else if (distance < 200) {
				return Math.ceil(distance / 10) * 10 + 'm';
			} else if (distance < 500) {
				return Math.round(distance / 50) * 50 + 'm';
			} else if (distance < 1000) {
				return Math.round(distance / 100) * 100 + 'm';
			} else {
				return (distance / 1000).toFixed(1) + 'km';
			}
		},
		onClickMarker(feature) {
			switch (feature.properties.type) {
				case 'accommodation':
					this.$gtag.event('click', {
						event_label: 'add-nearby-marker',
						event_category: 'accommodations-search-id'
					});
					this.$router.push(
						this.localePath({
							name: 'click',
							params: {
								id: feature.properties.id
							},
							hash: `#${this.filterstring}`
						})
					);
					break;
				case 'rental':
					this.$gtag.event('click', {
						event_label: 'rental-nearby-marker',
						event_category: 'accommodations-search-id'
					});
					window.open(
						`/redir?u=${encodeURIComponent(feature.properties.url)}&i=${
							feature.properties.id
						}&t=rental&h=${this.$redirHash(feature.properties.url)}&p=${
							JSON.parse(feature.properties.partner).slug
						}`,
						'_blank'
					);
					break;
				case 'skischool':
					this.$gtag.event('click', {
						event_label: 'skischool-nearby-marker',
						event_category: 'accommodations-search-id'
					});
					window.open(
						`/redir?u=${encodeURIComponent(feature.properties.url)}&i=${
							feature.properties.id
						}&t=skischool&h=${this.$redirHash(feature.properties.url)}&p=${
							JSON.parse(feature.properties.partner).slug
						}`,
						'_blank'
					);
					break;
			}
		},
		abortAlternatives() {
			if (this.$refs.otherweeks) {
				this.$refs.otherweeks.abortFetchRequest();
			}
			if (this.$refs.mapnearby) {
				this.$refs.mapnearby.abortFetchRequest();
			}
		}
	}
};
</script>
